import { useId } from 'react';
import { Card } from '../atoms/Card';
import { Checkbox } from '../atoms/Checkbox';
import { Grid } from '../atoms/Grid';
import { Stack } from '../atoms/Stack';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Section } from '../templates/Section';
import { useTranslation } from '../../i18n/useTranslation';
import { ZEOVIT_CONCERNS, ZEOVIT_CONCERN_NOTE_MAX, type ZeovitConcern } from '../../constants/zeovitConcerns';
import './ZeovitConcernsFieldset.css';

export interface ZeovitConcernsFieldsetProps {
  concerns: readonly ZeovitConcern[];
  note: string;
  onConcernsChange: (concerns: ZeovitConcern[]) => void;
  onNoteChange: (note: string) => void;
  /** Section description; the periodic check explains that empty keeps the earlier observations. */
  description?: string;
}

/**
 * "What isn't going well right now?" (ZeoVit dosing v3): optional user-reported
 * concerns as a group of Checkbox atoms (fieldset + legend) and a free-text note
 * (Textarea atom, max 1000 characters with a counter). Composed from atoms only.
 */
export function ZeovitConcernsFieldset({
  concerns,
  note,
  onConcernsChange,
  onNoteChange,
  description,
}: ZeovitConcernsFieldsetProps) {
  const { t } = useTranslation();
  const noteId = useId();
  const selected = new Set(concerns);

  const toggle = (concern: ZeovitConcern, checked: boolean) => {
    const next = new Set(selected);
    if (checked) next.add(concern);
    else next.delete(concern);
    // Keep the catalogue order so the request is stable.
    onConcernsChange(ZEOVIT_CONCERNS.filter((key) => next.has(key)));
  };

  return (
    <Section title={t('zeovit.concerns.title')} description={description ?? t('zeovit.concerns.description')}>
      <Card>
        <Stack gap="6">
          <fieldset className="zeovit-concerns__fieldset" data-testid="zeovit-concerns">
            <legend className="zeovit-concerns__legend">{t('zeovit.concerns.legend')}</legend>
            <Grid minItemWidth="sm" gap="3">
              {ZEOVIT_CONCERNS.map((concern) => (
                <Checkbox
                  key={concern}
                  name="concerns"
                  value={concern}
                  label={t(`zeovit.concern.${concern}`)}
                  checked={selected.has(concern)}
                  onChange={(event) => toggle(concern, event.target.checked)}
                />
              ))}
            </Grid>
          </fieldset>
          <Textarea
            id={noteId}
            label={t('zeovit.concerns.noteLabel')}
            value={note}
            maxLength={ZEOVIT_CONCERN_NOTE_MAX}
            rows={3}
            fullWidth
            helperText={t('zeovit.concerns.noteCounter', { count: note.length, max: ZEOVIT_CONCERN_NOTE_MAX })}
            onChange={(event) => onNoteChange(event.target.value.slice(0, ZEOVIT_CONCERN_NOTE_MAX))}
          />
        </Stack>
      </Card>
    </Section>
  );
}

export default ZeovitConcernsFieldset;
